import { Alert, Button, ConfirmActionModal } from '@shared/components';
import { FundingAuthorisation } from './FundingAuthorisation';
import { FundingChoiceCards } from './FundingChoiceCards';
import { InvoiceRejectionList } from './InvoiceRejectionList';
import { PaymentMethodSlot } from './PaymentMethodSlot';
import type { PaymentMethodOption } from './PaymentMethodSlot';
import { MotorplanPaymentPanel } from './MotorplanPaymentPanel';
import { TopUpForm } from './TopUpForm';
import {
    OTHER_PAYMENT_OPTIONS_LABEL,
    REPLACE_LINK_CANCEL_LABEL,
    RESEND_LINK_LABEL,
    REPLACE_LINK_CONFIRM_LABEL,
    REPLACE_LINK_TITLE,
    awaitingPaymentNotice,
    replaceLinkMessage,
} from '../utils/motorplanPaymentCopy';
import { fundingQuestion, topUpNotice } from '../utils/overBalanceCopy';
import { PAYMENT_METHOD_LABELS, paymentMethods } from '../utils/paymentMethods';
import type { PaymentMethod } from '../utils/paymentMethods';
import type { FundingAuthorisationProps } from './FundingAuthorisation';
import { useArrowKeyReveal } from '../hooks/useArrowKeyReveal';
import type { FundingChoiceState } from '../hooks/useFundingChoice';
import type { MotorplanPaymentState } from '../hooks/useMotorplanPayment';
import type { TopUpFormState } from '../hooks/useTopUpForm';
import type { AuthorisationFigures } from '../utils/fundingAuthorisation';
import type { TopUpPosting } from '../types/agreements.types';
import type { PaymentLinkSummary } from '../types/paymentLinks.types';

export interface InvoiceOverBalanceSectionProps {
    figures: AuthorisationFigures;
    fundingChoice: FundingChoiceState;
    potTotalAvailable: boolean;
    topUpForm: TopUpFormState;
    motorplan: MotorplanPaymentState;
    topUpPosting: TopUpPosting;
    locked: boolean;
    reasons: string[];
    authorisation: Omit<FundingAuthorisationProps, 'purpose' | 'claimed' | 'excess' | 'locked'>;
}

const WARNING_ID = 'invoice-over-balance-warning';
const TOP_UP_FORM_ID = 'invoice-top-up-form';
const MOTORPLAN_FORM_ID = 'invoice-motorplan-payment-form';

const initialMethod = (recordedAmount: string | null, sentLink: unknown): PaymentMethod | undefined => {
    if (recordedAmount !== null) return 'dealer';
    return sentLink ? 'motorplan' : undefined;
};

interface AwaitingPaymentAlertProps {
    link: PaymentLinkSummary;
    disabled: boolean;
    onResend: (link: PaymentLinkSummary) => void;
}

function AwaitingPaymentAlert({ link, disabled, onResend }: AwaitingPaymentAlertProps) {
    return (
        <Alert variant="info" role="status" className="flex-wrap justify-between">
            <p>{awaitingPaymentNotice(link)}</p>
            <Button type="button" variant="secondary" appearance="outline" size="sm" disabled={disabled} onClick={() => onResend(link)}>
                {RESEND_LINK_LABEL}
            </Button>
        </Alert>
    );
}

/** Rendered inside `PermissionGuard{invoice.add}`. */
export function InvoiceOverBalanceSection({ figures, fundingChoice, potTotalAvailable, topUpForm, motorplan, topUpPosting, locked, reasons, authorisation }: InvoiceOverBalanceSectionProps) {
    const { choice } = fundingChoice;
    const reveal = useArrowKeyReveal(fundingChoice.choose);
    const optionsHidden = motorplan.optionsHidden && choice === null;

    const forms: Record<PaymentMethod, PaymentMethodOption['renderForm']> = {
        dealer: (autoFocus) => (
            <>
                <TopUpForm form={topUpForm} id={TOP_UP_FORM_ID} autoFocusAmount={autoFocus} />
                {topUpForm.recordedAmount !== null && (
                    <p role="status" className="text-sm text-mp-foreground">
                        {topUpNotice(topUpForm.recordedAmount, topUpPosting)}
                    </p>
                )}
            </>
        ),
        motorplan: (autoFocus) => (
            <MotorplanPaymentPanel motorplan={motorplan} formId={MOTORPLAN_FORM_ID} autoFocus={autoFocus} locked={locked} />
        ),
    };
    const disabledReasons: Record<PaymentMethod, string | undefined> = {
        dealer: undefined,
        motorplan: motorplan.availability.available ? undefined : motorplan.availability.reason,
    };
    const methodOptions = paymentMethods().map((method) => ({
        value: method,
        label: PAYMENT_METHOD_LABELS[method],
        disabledReason: disabledReasons[method],
        renderForm: forms[method],
    }));

    return (
        <section aria-labelledby={WARNING_ID} className="space-y-4">
            <Alert variant="warning" role="note">
                <p id={WARNING_ID}>
                    The account balance doesn&apos;t cover this work. <strong>{figures.excess}</strong> needs to be
                    covered before work goes ahead.
                </p>
            </Alert>

            {motorplan.liveLink && (
                <AwaitingPaymentAlert
                    link={motorplan.liveLink}
                    disabled={locked || motorplan.form.isSubmitting}
                    onResend={motorplan.form.resend}
                />
            )}

            {motorplan.form.resendError && (
                <Alert variant="error">
                    <p>{motorplan.form.resendError}</p>
                </Alert>
            )}

            <InvoiceRejectionList messages={reasons} />

            {optionsHidden ? (
                <Button type="button" variant="secondary" appearance="outline" size="sm" disabled={locked} onClick={motorplan.revealOptions}>
                    {OTHER_PAYMENT_OPTIONS_LABEL}
                </Button>
            ) : (
                <FundingChoiceCards
                    question={fundingQuestion(figures.excess)}
                    choice={choice}
                    locked={locked}
                    potTotalAvailable={potTotalAvailable}
                    reveal={reveal}
                    focusOnMount={motorplan.optionsRevealed}
                />
            )}

            {choice === 'top_up' && (
                <PaymentMethodSlot
                    options={methodOptions}
                    autoFocus={reveal.focusOnReveal}
                    locked={locked}
                    initialSelected={initialMethod(topUpForm.recordedAmount, motorplan.form.sentLink)}
                />
            )}

            {(choice === 'arrears' || choice === 'pot_total') && (
                <FundingAuthorisation
                    {...authorisation}
                    purpose={choice}
                    claimed={figures.claimed}
                    excess={figures.excess}
                    locked={locked}
                />
            )}

            <ConfirmActionModal
                isOpen={motorplan.form.replacement !== null}
                title={REPLACE_LINK_TITLE}
                message={motorplan.form.replacement ? replaceLinkMessage(motorplan.form.replacement) : ''}
                confirmLabel={REPLACE_LINK_CONFIRM_LABEL}
                cancelLabel={REPLACE_LINK_CANCEL_LABEL}
                confirmVariant="warning"
                isPending={motorplan.form.replacing}
                onConfirm={() => void motorplan.form.confirmReplace()}
                onClose={motorplan.form.keepExisting}
            />
        </section>
    );
}
